import { CloudflareImage } from '@components/primitives/Image/Cloudflare'
import Link from '@components/primitives/Link'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import type { TextColor } from '@lib/colors/colors'
import kebabCase from 'lodash/kebabCase'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

export function ConjuredArticleMarkdown({
  markdown,
  pStyle = 'p',
  textColor,
}: {
  markdown: string
  pStyle?: 'p' | 'p-small'
  textColor?: TextColor
}) {
  return (
    <ReactMarkdown
      components={{
        a: ({ children, href }) => (
          <Link className={textColor ?? 'text-primary700'} href={href!} newTab>
            {children}
          </Link>
        ),
        h2: ({ children }) => (
          <Text
            className='mt-12 pb-4'
            element='h3'
            id={kebabCase(children?.toString())}
            styleName='h3'
          >
            {children}
          </Text>
        ),
        h3: ({ children }) => (
          <Text className='py-4' element='h3' styleName='h5'>
            {children}
          </Text>
        ),
        h4: ({ children }) => (
          <Text className='py-2' element='h4' styleName='h6'>
            {children}
          </Text>
        ),
        img: ({ alt, src }) => (
          <CloudflareImage
            alt={alt!}
            className='w-full rounded-xl'
            height={765}
            imageId={src!} // use the src as the imageId
            width={1366} // Midjourney images are usually this dimension since we use 16:9
          />
        ),
        li: ({ children }) => (
          <li className={classMerge('ml-4', textColor)}>
            <Text className={textColor} styleName={pStyle}>
              {children}
            </Text>
          </li>
        ),
        p: ({ children }) => (
          <Text
            className={textColor ?? 'text-neutral800'}
            element='p'
            styleName={pStyle}
          >
            {children}
          </Text>
        ),
        strong: ({ children }) => (
          <Text
            className={classMerge('inline', textColor ?? 'text-neutral900')}
            element='span'
            styleName='p-strong'
          >
            {children}
          </Text>
        ),
        table: ({ children }) => (
          <table className='w-full border border-neutral300 shadow-md'>
            {children}
          </table>
        ),
        td: ({ children }) => (
          <td
            className={
              'p-1.5 text-xs text-neutral700 first:bg-neutral50 first:font-bold first:text-neutral800 sm:p-3 md:text-sm'
            }
          >
            {children}
          </td>
        ),
        th: ({ children }) => (
          <th
            className={
              'bg-neutral100 p-1.5 text-sm text-neutral900 sm:px-3 sm:py-2 md:text-base'
            }
          >
            {children}
          </th>
        ),
        tr: ({ children }) => (
          <tr className='border-b border-neutral300 text-left'>{children}</tr>
        ),
        ul: ({ children }) => <ul className='ml-8 list-disc'>{children}</ul>,
      }}
      remarkPlugins={[remarkGfm]}
    >
      {markdown}
    </ReactMarkdown>
  )
}
